import styled, { css } from 'styled-components';

import { CARD_HEIGHT } from '@/components/Card/Card.constants';
import { getStatusColor } from '@/components/Card/Card.helpers';
import { Button } from '@/components/ui/Button/Button';
import { getVariant } from '@/components/ui/Typography/Typography.styles';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { CardStatus } from '@/constants/common';
import {
  ellipsisText,
  flexBetween,
  flexCenterAligned,
  flexColumn
} from '@/styles/common';

export const CardWrapper = styled.div<{
  $isDeactivated: boolean;
  $isLink: boolean;
  $isResume?: boolean;
  $isMine?: boolean;
}>`
  ${({ $isDeactivated, $isLink }) =>
    !$isDeactivated && $isLink && 'cursor: pointer;'}
  width: 100%;
  background-color: ${({ $isResume }) =>
    $isResume ? 'var(--bg-main)' : 'var(--bg-card)'};
  border-radius: 28px;
  padding: 20px;
  ${flexColumn};
  gap: 16px;
  color: var(--textAndLabels-main);
  border: 1px solid var(--border-secondary);
  height: ${({ $isMine }) =>
    $isMine ? CARD_HEIGHT.profile : CARD_HEIGHT.public};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    height: ${({ $isMine }) =>
      $isMine ? CARD_HEIGHT.profileMobile : CARD_HEIGHT.public};
  }
`;

export const CardHeader = styled.div`
  ${flexBetween};
  gap: 8px;
`;

export const CompanyInfo = styled.div<{ $isDeactivated: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  opacity: ${({ $isDeactivated }) => ($isDeactivated ? '0.5' : '1')};
  min-width: 0;
`;

export const CompanyName = styled.p`
  ${getVariant('body2')};
  ${ellipsisText};
  white-space: nowrap;
`;

export const AddToBookmarksButton = styled.button<{
  $isBookmarked: boolean;
  $isDeactivated: boolean;
}>`
  ${flexCenterAligned};
  width: 22px;
  height: 22px;
  background: none;
  border: none;
  ${({ $isDeactivated }) => !$isDeactivated && 'cursor: pointer;'}

  svg {
    transition: all 0.3s ease;
    color: ${({ $isBookmarked, $isDeactivated }) =>
      $isDeactivated
        ? 'var(--textAndLabels-tertiary)'
        : $isBookmarked
          ? 'var(--system-red)'
          : 'var(--textAndLabels-main)'};

    fill: ${({ $isBookmarked }) =>
      $isBookmarked ? 'var(--system-red)' : 'var(--bookmark-fill)'};
  }
  ${({ $isDeactivated, $isBookmarked }) =>
    !$isDeactivated &&
    css`
      &:hover {
        svg {
          fill: ${$isBookmarked
            ? 'var(--system-red)'
            : 'var(--bookmark-hover-fill)'};
        }
      }
    `}
`;

export const Status = styled.p<{ $status?: CardStatus }>`
  ${getVariant('caption1')};
  color: ${({ $status }) => getStatusColor($status)};
  flex-shrink: 0;
`;

export const CardBody = styled.div`
  ${flexColumn};
  gap: 10px;
`;

export const JobTitle = styled.p<{ $isDeactivated: boolean }>`
  display: -webkit-box;
  max-width: 100%;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  ${ellipsisText};
  ${getVariant('title3')};
  opacity: ${({ $isDeactivated }) => ($isDeactivated ? '0.5' : '1')};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    ${getVariant('title4')};
  }
`;

export const Salary = styled.h4<{ $isDeactivated: boolean }>`
  ${getVariant('h4')};

  color: ${({ $isDeactivated }) =>
    $isDeactivated
      ? 'var(--textAndLabels-tertiary)'
      : 'var(--textAndLabels-main)'};
`;

export const CardActions = styled.div`
  ${flexColumn};
  gap: 14px;
  margin-top: auto;
`;

export const CardFooter = styled.div`
  ${flexBetween};
  gap: 10px;
`;

export const LocationDate = styled.div<{ $isDeactivated: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--textAndLabels-tertiary);
  ${getVariant('caption1')};
  opacity: ${({ $isDeactivated }) => ($isDeactivated ? '0.5' : '1')};
  white-space: nowrap;
  overflow: hidden;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    ${getVariant('caption3')};
  }
`;

export const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: var(--textAndLabels-tertiary);
  align-self: center;
  flex-shrink: 0;
`;

export const BottomActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const ChipsWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

export const Chip = styled.div`
  ${flexCenterAligned};
  width: 20px;
  height: 20px;
  color: var(--textAndLabels-main);
`;

export const MenuWrapper = styled.div`
  position: relative;
`;

export const MenuButton = styled.button<{ $isOpen: boolean }>`
  ${flexCenterAligned};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 20px;
  height: 20px;
  color: var(--textAndLabels-main);
  transition: all 0.3s ease;
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: var(--bg-main);
  border-radius: 28px;
  box-shadow: 0 17px 34px 0 rgba(151, 151, 151, 0.08);
  z-index: 1;
  width: 240px;
  padding: 10px 5px;
`;

export const DropdownItem = styled.div`
  padding: 13px 15px;
  cursor: pointer;
  color: var(--textAndLabels-main);
  transition: color 0.3s ease;

  &:hover {
    color: var(--custom-yellow);
  }
`;

export const ProgressBarWrapper = styled.div`
  ${flexColumn};
  gap: 10px;
  color: var(--textAndLabels-tertiary);
`;

export const ProgressBar = styled.div<{
  $progress: number;
}>`
  height: 5px;
  background: var(--border-secondary);
  border-radius: 100px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: ${({ $progress }) => `${$progress}%`};
    height: 100%;
    background: var(--textAndLabels-main);
    border-radius: 100px;
    transition: width 0.3s ease;
  }
`;

export const PromoteButton = styled(Button).attrs({
  variant: 'primary',
  size: 's',
  fullWidth: true
})`
  background-color: var(--system-black);
  color: var(--promote-button-text);
  padding: 11px 18px 11px 13px;

  &:hover {
    background-color: #000;
  }
`;

export const PayButton = styled(Button)`
  width: 100%;
  flex: 1;
  background-color: var(--system-green);
`;

export const ActivateButton = styled(Button).attrs({
  variant: 'primary',
  size: 's',
  fullWidth: true
})`
  background-color: var(--system-deepBlue);
  color: var(--bg-main);

  &:hover {
    background-color: var(--system-blue);
  }
`;
